import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  CheckCircle, Circle, Sparkle, ArrowRight, MagnifyingGlass,
} from "@phosphor-icons/react";

/**
 * Public Early Access status tracker.
 * URL: /waitlist/status?email=you@org.co  (or type email in the field).
 * Renders a 5-step timeline (waiting → qualified → priority → invited → subscription ready).
 */
export default function WaitlistStatus() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [locale, setLocale] = useState(
    typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("fr") ? "fr" : "en"
  );
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const isFr = locale === "fr";
  const t = (fr, en) => (isFr ? fr : en);

  const lookup = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!email) return;
    setBusy(true); setErr(null); setData(null);
    try {
      const { data } = await api.get(`/waitlist/status`, { params: { email: email.trim().toLowerCase(), locale } });
      setData(data);
    } catch (er) {
      setErr(extractApiError(er, t("Aucune demande trouvée pour cet email.", "No application found for this email.")));
    }
    setBusy(false);
  };

  useEffect(() => {
    if (params.get("email")) lookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="waitlist-status-page">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Logo variant="wordmark" size={30} to="/" testId="wls-logo" />
          <button
            type="button"
            onClick={() => setLocale(isFr ? "en" : "fr")}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5"
            data-testid="wls-locale-toggle"
          >
            {isFr ? "EN" : "FR"}
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="inline-flex items-center gap-2 bg-card border border-border text-xs font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full mb-6">
          <Sparkle size={12} weight="fill" className="text-gold" />
          {t("Suivi de votre accès anticipé", "Your Early Access application")}
        </div>
        <h1 className="font-display text-3xl sm:text-5xl tracking-tight leading-[1.05]">
          {t("Où en est votre", "Where is your")}<br />
          <span className="italic text-primary">{t("candidature ?", "application?")}</span>
        </h1>
        <p className="mt-5 text-sm text-muted-foreground max-w-lg leading-relaxed">
          {t(
            "Entrez l'email utilisé lors de votre inscription pour voir votre progression et votre position dans la file d'activation prioritaire.",
            "Enter the email you registered with to see your progress and priority activation position."
          )}
        </p>

        <form onSubmit={lookup} className="mt-8 flex gap-2 max-w-md" data-testid="wls-form">
          <input
            type="email" required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("votre@email.co", "your@email.co")}
            className="flex-1 h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="wls-email-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-4 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            data-testid="wls-lookup"
          >
            <MagnifyingGlass size={14} weight="bold" /> {t("Vérifier", "Check")}
          </button>
        </form>

        {err && <div className="mt-6 rounded-lg border border-red-200 bg-red-50 text-red-700 p-4 text-sm" data-testid="wls-error">{err}</div>}

        {data && (
          <div className="mt-10 bg-card border border-border rounded-2xl p-6 sm:p-10 card-elev" data-testid="wls-result">
            <div className="text-overline text-primary mb-2">{t("Statut actuel", "Current status")}</div>
            <div className="font-display text-2xl sm:text-3xl tracking-tight" data-testid="wls-current-label">{data.current_label}</div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{data.current_note}</p>

            <ol className="mt-10 space-y-6" data-testid="wls-timeline">
              {data.timeline.map((step, i) => {
                const isDone = step.state === "done";
                const isCurrent = step.state === "current";
                return (
                  <li key={step.key} className="flex gap-4" data-testid={`wls-step-${step.key}`}>
                    <div className="flex flex-col items-center">
                      <div className={`h-9 w-9 rounded-full grid place-items-center border-2 transition-colors ${
                        isDone ? "bg-teal-soft border-teal text-teal" :
                        isCurrent ? "bg-primary border-primary text-primary-foreground animate-pulse" :
                        "bg-secondary border-border text-muted-foreground"
                      }`}>
                        {isDone ? <CheckCircle size={16} weight="fill" /> : <span className="font-mono text-xs font-bold">{i + 1}</span>}
                      </div>
                      {i < data.timeline.length - 1 && (
                        <div className={`w-0.5 flex-1 my-1 min-h-[24px] ${isDone ? "bg-teal" : "bg-border"}`} />
                      )}
                    </div>
                    <div className="pb-6">
                      <div className={`font-semibold text-sm ${isCurrent ? "text-primary" : ""}`}>{step.label}</div>
                      <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{step.note}</div>
                    </div>
                  </li>
                );
              })}
            </ol>

            <div className="mt-8 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">
                {t("Inscrit(e) le", "Registered on")} <span className="font-semibold text-foreground">{data.created_at ? new Date(data.created_at).toLocaleDateString() : "—"}</span>
                {data.preferred_plan && (
                  <> · {t("Plan préféré", "Preferred plan")}: <span className="font-semibold text-foreground capitalize">{data.preferred_plan}</span></>
                )}
              </div>
              <a
                href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 border border-border rounded-lg px-4 py-2 text-xs font-semibold hover:bg-secondary"
                data-testid="wls-book-briefing"
              >
                {t("Réserver une démonstration produit", "Book a product demo")} <ArrowRight size={12} weight="bold" />
              </a>
            </div>
          </div>
        )}

        <div className="mt-10 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground" data-testid="wls-home-link">
            ← {t("Retour à l'accueil", "Back to home")}
          </Link>
        </div>
      </div>
    </div>
  );
}
